/**
 * EvidenceViewerModal · Wave 392 · 2026-05-14
 *
 * Modal shell for the Evidence Viewer. Rendered by EvidenceViewerProvider
 * via createPortal(document.body). Mounts permanently, reads isOpen from context.
 *
 * Design doc Section 4.2:
 *   - header: row label + close button
 *   - body: switch on docType → PdfEvidenceViewer | IfcEvidenceViewer | fallback
 *   - footer: prev/next evidence arrows (when siblings > 1)
 *
 * Lazy imports:
 *   PdfEvidenceViewer — loaded only when docType='pdf' (keeps vendor-pdfjs chunk
 *   out of the initial page load)
 *   IfcEvidenceViewer — stub; Agent F replaces the file content
 *
 * Keyboard nav is handled by the context (Escape, ArrowLeft, ArrowRight).
 * ArrowUp/ArrowDown for page nav is handled inside PdfEvidenceViewer.
 */

import { Suspense, lazy, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useEvidenceViewer } from './EvidenceViewerContext';
import { useT } from '@/hooks/useT';
import { cn } from '@/lib/cn';

// ─── Lazy imports ─────────────────────────────────────────────────────────────

// PdfEvidenceViewer is lazy so the vendor-pdfjs chunk is NOT fetched until
// a PDF chip is clicked. Confirmed separate via vite build manifest.
const PdfEvidenceViewer = lazy(() =>
  import('./PdfEvidenceViewer').then((m) => ({ default: m.PdfEvidenceViewer })),
);

// IfcEvidenceViewer stub — Agent F replaces the file content.
const IfcEvidenceViewer = lazy(() =>
  import('./IfcEvidenceViewer').then((m) => ({ default: m.IfcEvidenceViewer })),
);

// ─── TextOnlyFallback ─────────────────────────────────────────────────────────

function TextOnlyFallback({ quote }: { quote: string }) {
  const { t } = useT();
  return (
    <div className="flex flex-col gap-3 py-6 px-4">
      <p className="text-[10px] text-ink-muted uppercase tracking-wide">
        {t.evidenceViewer.citationLabel}
      </p>
      <blockquote className="border-l-2 border-warn/50 pl-3 text-sm text-ink-muted italic">
        {quote || '—'}
      </blockquote>
    </div>
  );
}

// ─── EvidenceViewerModal ──────────────────────────────────────────────────────

export function EvidenceViewerModal() {
  const { isOpen, current, siblings, siblingIndex, closeViewer, goNext, goPrev, viewMode } =
    useEvidenceViewer();
  const { t } = useT();
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const prevFocusRef = useRef<Element | null>(null);

  // Focus management: move focus to close button on open, restore on close
  useEffect(() => {
    if (isOpen) {
      prevFocusRef.current = document.activeElement;
      closeButtonRef.current?.focus();
    } else {
      if (prevFocusRef.current instanceof HTMLElement) {
        prevFocusRef.current.focus();
      }
      prevFocusRef.current = null;
    }
  }, [isOpen]);

  // Panel mode: the side panel handles rendering; modal is suppressed.
  if (viewMode === 'panel') return null;

  if (!isOpen || !current) return null;

  const { projectId, estimateId, docId, docType, page, bbox, globalIds, quote, rowLabel } = current;
  const hasSiblings = siblings.length > 1;

  const modal = (
    <div
      className="fixed inset-0 z-[9000] flex items-center justify-center p-4"
      aria-modal="true"
      role="dialog"
      aria-labelledby="evidence-viewer-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={closeViewer}
        aria-hidden="true"
      />

      {/* Dialog panel */}
      <div className="relative z-10 w-full max-w-2xl max-h-[90vh] flex flex-col rounded-xl bg-surface-1 border border-line shadow-2xl overflow-hidden">

        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-line shrink-0">
          <div className="min-w-0">
            <h2
              id="evidence-viewer-title"
              className="text-sm font-semibold text-ink truncate"
            >
              {rowLabel}
            </h2>
            {docType !== 'unknown' && (
              <span className="text-[10px] text-ink-muted uppercase tracking-wide">
                {docType === 'pdf' ? 'PDF' : 'IFC'}
              </span>
            )}
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            aria-label={t.evidenceViewer.close}
            onClick={closeViewer}
            className="shrink-0 rounded p-1 text-ink-muted hover:text-ink hover:bg-surface-2 transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto px-4 py-4">
          <Suspense
            fallback={
              <div className="flex items-center justify-center py-12 text-ink-subtle text-sm">
                {t.evidenceViewer.loadingDoc}
              </div>
            }
          >
            {docType === 'pdf' && (
              <PdfEvidenceViewer
                projectId={projectId}
                estimateId={estimateId}
                docId={docId}
                page={page}
                bbox={bbox}
                quote={quote}
                i18n={{
                  loadingDoc:      t.evidenceViewer.loadingDoc,
                  errorLoadingDoc: t.evidenceViewer.errorLoadingDoc,
                  noDocStored:     t.evidenceViewer.noDocStored,
                  prevPage:        t.evidenceViewer.prevPage,
                  nextPage:        t.evidenceViewer.nextPage,
                  pageOf:          t.evidenceViewer.pageOf,
                  citationLabel:   t.evidenceViewer.citationLabel,
                  pdfAriaLabel:    t.evidenceViewer.pdfAriaLabel,
                }}
              />
            )}
            {docType === 'ifc' && (
              <IfcEvidenceViewer
                projectId={projectId}
                estimateId={estimateId}
                docId={docId}
                bbox={bbox}
                quote={quote}
                targetGlobalIds={globalIds}
                i18n={{
                  loadingDoc:      t.evidenceViewer.loadingDoc,
                  errorLoadingDoc: t.evidenceViewer.errorLoadingDoc,
                  noDocStored:     t.evidenceViewer.noDocStored,
                  ifcNoElement:    t.evidenceViewer.ifcNoElement,
                  ifcFallbackTitle: t.evidenceViewer.ifcFallbackTitle,
                  ifcAriaLabel:    t.evidenceViewer.ifcAriaLabel,
                  citationLabel:   t.evidenceViewer.citationLabel,
                }}
              />
            )}
            {docType === 'unknown' && (
              <TextOnlyFallback quote={quote} />
            )}
          </Suspense>
        </div>

        {/* Footer — prev/next evidence navigation */}
        {hasSiblings && (
          <div className="flex items-center justify-between px-4 py-2 border-t border-line shrink-0 bg-surface-2">
            <button
              type="button"
              aria-label={t.evidenceViewer.prevEvidence}
              onClick={goPrev}
              className={cn(
                'rounded px-3 py-1 text-xs border border-line',
                'hover:bg-surface-3 transition-colors',
                siblingIndex === 0 && 'opacity-40 cursor-default pointer-events-none',
              )}
            >
              ← {t.evidenceViewer.prevEvidence}
            </button>
            <span className="text-[11px] text-ink-muted tabular-nums">
              {siblingIndex + 1} / {siblings.length}
            </span>
            <button
              type="button"
              aria-label={t.evidenceViewer.nextEvidence}
              onClick={goNext}
              className={cn(
                'rounded px-3 py-1 text-xs border border-line',
                'hover:bg-surface-3 transition-colors',
                siblingIndex === siblings.length - 1 && 'opacity-40 cursor-default pointer-events-none',
              )}
            >
              {t.evidenceViewer.nextEvidence} →
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
